<script lang="ts">
  // Paginated list of users — followers OR following — for any username.
  // Used by both the public profile page (tabs on /u/:username) and the
  // account page (tab on /account). Owns its own fetch lifecycle and
  // pagination state; the parent just hands it a username + which list
  // to show.
  //
  // Each row is a clickable Link to that user's profile, plus a small
  // FollowButton scoped to the VIEWER's relation (so the button on a
  // row in your "Followers" list says Follow / Following based on
  // whether YOU follow that follower of yours).

  import { Link } from 'svelte-routing';
  import { formatRelativeTime } from '@atlasguess/games';
  import DonorIcon from './DonorIcon.svelte';
  import FollowButton from './FollowButton.svelte';
  import Spinner from './ui/Spinner.svelte';
  import {
    fetchFollowers,
    fetchFollowing,
    type FollowListRow,
  } from '../lib/follows';

  type Kind = 'followers' | 'following';

  interface Props {
    username: string;
    kind: Kind;
  }

  let { username, kind }: Props = $props();

  let rows = $state<FollowListRow[]>([]);
  let page = $state(1);
  let lastPage = $state(1);
  let total = $state(0);
  let loading = $state(true);
  let errored = $state(false);

  async function load(p: number): Promise<void> {
    loading = true;
    errored = false;
    try {
      const fetcher = kind === 'followers' ? fetchFollowers : fetchFollowing;
      const res = await fetcher(username, p);
      rows = res.rows;
      page = res.page;
      lastPage = res.last_page;
      total = res.total;
    } catch {
      errored = true;
      rows = [];
    } finally {
      loading = false;
    }
  }

  // Re-fetch on prop change. The parent flipping tabs (kind change) or
  // navigating between profiles (username change) both trigger a fresh
  // page-1 fetch.
  $effect(() => {
    // Read both reactive props so $effect tracks them.
    void username;
    void kind;
    void load(1);
  });

  const emptyMessage = $derived(
    kind === 'followers' ? 'No followers yet.' : 'Not following anyone yet.',
  );
</script>

<div class="follows-list-wrap">
  {#if loading && rows.length === 0}
    <div class="follows-list__loading"><Spinner size="md" /></div>
  {:else if errored}
    <p class="follows-list__empty">Couldn't load this list. Please try again.</p>
  {:else if rows.length === 0}
    <p class="follows-list__empty">{emptyMessage}</p>
  {:else}
    <ul class="follows-list">
      {#each rows as row (row.username)}
        <li class="follows-list__row">
          <Link
            to={`/u/${row.username}`}
            getProps={() => ({ class: 'follows-list__user' })}
          >
            <DonorIcon tier={row.donor_tier} size={28} />
            <span class="follows-list__name">{row.username}</span>
          </Link>
          <span class="follows-list__time">
            {formatRelativeTime(new Date(row.followed_at).getTime())}
          </span>
          <FollowButton username={row.username} size="sm" />
        </li>
      {/each}
    </ul>

    {#if lastPage > 1}
      <nav class="follows-list__pagination" aria-label="Pagination">
        <button
          type="button"
          class="follows-list__page-btn"
          disabled={page === 1 || loading}
          onclick={() => load(page - 1)}
        >
          ‹ Previous
        </button>
        <span class="follows-list__page-info">
          Page {page} of {lastPage} · {total.toLocaleString()} total
        </span>
        <button
          type="button"
          class="follows-list__page-btn"
          disabled={page === lastPage || loading}
          onclick={() => load(page + 1)}
        >
          Next ›
        </button>
      </nav>
    {/if}
  {/if}
</div>
